if (!customElements.get('festive-animation')) {
  class FestiveAnimation extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this.dataset.showMobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;

      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.effect = this.dataset.effect || 'both';
      this.colors = (this.dataset.colors || '').split(',').filter(Boolean);
      if (!this.colors.length) this.colors = ['#FFD54F', '#FF8F00', '#E53935'];
      this.frequency = parseInt(this.dataset.frequency, 10) || 4;
      this.particleCount = parseInt(this.dataset.particles, 10) || 45;
      this.sizeFactor = (parseInt(this.dataset.size, 10) || 4) / 4;
      // Smaller screens get proportionally smaller bursts so they don't cover the page.
      this.screenScale = window.innerWidth < 750 ? 0.7 : 1;
      this.stopAfter = parseFloat(this.dataset.stopAfter) * 1000 || 0;

      this.rockets = [];
      this.sparks = [];
      this.sparkles = [];
      this.startTime = performance.now();
      this.nextLaunch = this.startTime + 400;

      this.resize = this.resize.bind(this);
      this.loop = this.loop.bind(this);
      this.onVisibility = this.onVisibility.bind(this);

      this.resize();
      window.addEventListener('resize', this.resize);
      document.addEventListener('visibilitychange', this.onVisibility);

      if (this.effect !== 'fireworks') {
        const count = parseInt(this.dataset.sparkles, 10) || 0;
        for (let i = 0; i < count; i++) this.sparkles.push(this.createSparkle());
      }

      if (this.effect !== 'sparkles') {
        const intro = parseInt(this.dataset.introBursts, 10) || 0;
        for (let i = 0; i < intro; i++) {
          setTimeout(() => this.launch(), i * 250 + Math.random() * 200);
        }
      }

      this.frame = requestAnimationFrame(this.loop);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      window.removeEventListener('resize', this.resize);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    onVisibility() {
      cancelAnimationFrame(this.frame);
      if (!document.hidden && !this.stopped) {
        this.nextLaunch = performance.now() + 300;
        this.frame = requestAnimationFrame(this.loop);
      }
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.width = window.innerWidth;
      this.height = window.innerHeight;
      this.canvas.width = this.width * dpr;
      this.canvas.height = this.height * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    random(min, max) {
      return min + Math.random() * (max - min);
    }

    pickColor() {
      return this.colors[Math.floor(Math.random() * this.colors.length)];
    }

    createSparkle() {
      return {
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        size: this.random(2, 4),
        phase: Math.random() * Math.PI * 2,
        speed: this.random(0.02, 0.05),
        color: this.pickColor(),
      };
    }

    launch() {
      if (this.stopping) return;
      const x = this.random(this.width * 0.1, this.width * 0.9);
      const targetY = this.random(this.height * 0.12, this.height * 0.5);
      this.rockets.push({
        x,
        y: this.height,
        px: x,
        py: this.height,
        vx: this.random(-0.6, 0.6),
        vy: -Math.sqrt(2 * 0.06 * (this.height - targetY)),
        color: this.pickColor(),
      });
    }

    explode(rocket) {
      const count = Math.round(this.particleCount * this.random(0.8, 1.2));
      const secondColor = Math.random() < 0.5 ? this.pickColor() : rocket.color;
      const power = this.random(4.5, 6.5) * this.sizeFactor * this.screenScale;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + this.random(-0.1, 0.1);
        const speed = power * this.random(0.4, 1);
        this.sparks.push({
          x: rocket.x,
          y: rocket.y,
          px: rocket.x,
          py: rocket.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          alpha: 1,
          decay: this.random(0.008, 0.014),
          color: i % 2 ? rocket.color : secondColor,
          width: this.random(2, 3.5) * this.screenScale,
        });
      }
    }

    loop(now) {
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.width, this.height);

      if (this.stopAfter && now - this.startTime > this.stopAfter) this.stopping = true;

      if (this.effect !== 'sparkles' && !this.stopping && now >= this.nextLaunch) {
        this.launch();
        const interval = 10000 / this.frequency;
        this.nextLaunch = now + interval * this.random(0.6, 1.4);
      }

      ctx.lineCap = 'round';

      for (let i = this.rockets.length - 1; i >= 0; i--) {
        const r = this.rockets[i];
        r.px = r.x;
        r.py = r.y;
        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.06;
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = r.color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(r.px, r.py + 14);
        ctx.lineTo(r.x, r.y);
        ctx.stroke();
        if (r.vy >= 0) {
          this.explode(r);
          this.rockets.splice(i, 1);
        }
      }

      for (let i = this.sparks.length - 1; i >= 0; i--) {
        const s = this.sparks[i];
        s.px = s.x;
        s.py = s.y;
        s.vx *= 0.97;
        s.vy = s.vy * 0.97 + 0.035;
        s.x += s.vx;
        s.y += s.vy;
        s.alpha -= s.decay;
        if (s.alpha <= 0) {
          this.sparks.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = s.alpha;
        ctx.strokeStyle = s.color;
        ctx.lineWidth = s.width;
        ctx.beginPath();
        ctx.moveTo(s.px, s.py);
        ctx.lineTo(s.x, s.y);
        ctx.stroke();
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.width * 0.8, 0, Math.PI * 2);
        ctx.fill();
      }

      const sparkleFade = this.stopping ? Math.max(0, 1 - (now - this.startTime - this.stopAfter) / 1500) : 1;
      for (const sp of this.sparkles) {
        sp.phase += sp.speed;
        const a = ((Math.sin(sp.phase) + 1) / 2) * sparkleFade;
        if (a < 0.05) {
          if (Math.sin(sp.phase) < -0.99) Object.assign(sp, this.createSparkle(), { phase: sp.phase });
          continue;
        }
        ctx.globalAlpha = a;
        ctx.fillStyle = sp.color;
        const len = sp.size * 2.5 * a;
        ctx.beginPath();
        ctx.moveTo(sp.x, sp.y - len);
        ctx.lineTo(sp.x + sp.size * 0.5, sp.y);
        ctx.lineTo(sp.x, sp.y + len);
        ctx.lineTo(sp.x - sp.size * 0.5, sp.y);
        ctx.closePath();
        ctx.moveTo(sp.x - len, sp.y);
        ctx.lineTo(sp.x, sp.y + sp.size * 0.5);
        ctx.lineTo(sp.x + len, sp.y);
        ctx.lineTo(sp.x, sp.y - sp.size * 0.5);
        ctx.closePath();
        ctx.fill();
      }

      ctx.globalAlpha = 1;

      if (this.stopping && !this.rockets.length && !this.sparks.length && sparkleFade <= 0) {
        this.stopped = true;
        ctx.clearRect(0, 0, this.width, this.height);
        return;
      }

      this.frame = requestAnimationFrame(this.loop);
    }
  }

  customElements.define('festive-animation', FestiveAnimation);
}
